import { EXPORT_THEME, formatExportTimestamp, loadExportLogoBuffer } from '../theme';

let excelJsModule;

async function getExcelJS() {
  if (!excelJsModule) {
    excelJsModule = (await import('exceljs')).default;
  }
  return excelJsModule;
}

const THIN_BORDER = {
  top: { style: 'thin', color: { argb: EXPORT_THEME.border } },
  left: { style: 'thin', color: { argb: EXPORT_THEME.border } },
  bottom: { style: 'thin', color: { argb: EXPORT_THEME.border } },
  right: { style: 'thin', color: { argb: EXPORT_THEME.border } },
};

export function getThinBorder() {
  return THIN_BORDER;
}

export function getHeaderStyle() {
  return {
    font: { bold: true, color: { argb: EXPORT_THEME.white }, size: 11 },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_THEME.brandGreen } },
    alignment: { vertical: 'middle', horizontal: 'left', wrapText: true },
    border: THIN_BORDER,
  };
}

export function getBodyStyle(isStripe = false) {
  return {
    font: { color: { argb: EXPORT_THEME.bodyText }, size: 10 },
    fill: isStripe
      ? { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_THEME.stripeFill } }
      : undefined,
    alignment: { vertical: 'top', horizontal: 'left', wrapText: true },
    border: THIN_BORDER,
  };
}

export async function downloadWorkbook(workbook, filename) {
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function addBrandedHeader(worksheet, workbook, {
  title,
  subtitle,
  totalColumns,
}) {
  const columns = Math.max(totalColumns, 4);
  worksheet.mergeCells(1, 2, 2, columns);
  worksheet.mergeCells(3, 2, 3, columns);

  const orgCell = worksheet.getCell(1, 2);
  orgCell.value = EXPORT_THEME.organization;
  orgCell.font = { bold: true, size: 14, color: { argb: EXPORT_THEME.brandGreen } };
  orgCell.alignment = { vertical: 'middle', horizontal: 'left' };

  const titleCell = worksheet.getCell(2, 2);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 12, color: { argb: EXPORT_THEME.bodyText } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

  const subtitleCell = worksheet.getCell(3, 2);
  subtitleCell.value = subtitle;
  subtitleCell.font = { size: 10, color: { argb: EXPORT_THEME.mutedText } };
  subtitleCell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

  worksheet.getRow(1).height = 24;
  worksheet.getRow(2).height = 22;
  worksheet.getRow(3).height = 18;

  const logoBuffer = await loadExportLogoBuffer();
  if (logoBuffer) {
    const imageId = workbook.addImage({
      buffer: logoBuffer,
      extension: 'png',
    });
    worksheet.addImage(imageId, {
      tl: { col: 0.1, row: 0.1 },
      ext: { width: 56, height: 56 },
    });
    worksheet.getColumn(1).width = 10;
  }

  return 5;
}

export function applyAutoColumnWidths(worksheet, {
  fromRow,
  fromCol,
  toCol,
  minWidth = 12,
  maxWidth = 48,
  sampleRowCount = 200,
}) {
  const lastRow = Math.min(worksheet.rowCount, fromRow + sampleRowCount);

  for (let col = fromCol; col <= toCol; col += 1) {
    let maxLength = 10;
    for (let row = fromRow; row <= lastRow; row += 1) {
      const value = worksheet.getCell(row, col).value;
      const text = value == null ? '' : String(value);
      maxLength = Math.max(maxLength, text.length);
    }
    worksheet.getColumn(col).width = Math.min(maxWidth, Math.max(minWidth, maxLength + 2));
  }
}

export function applyTableAutofilter(worksheet, headerRow, fromCol, toCol) {
  worksheet.autoFilter = {
    from: { row: headerRow, column: fromCol },
    to: { row: headerRow, column: toCol },
  };
  worksheet.views = [{ state: 'frozen', ySplit: headerRow, activeCell: `A${headerRow + 1}` }];
}

export async function createStyledWorkbook(sheetName) {
  const ExcelJS = await getExcelJS();
  const workbook = new ExcelJS.Workbook();
  workbook.creator = EXPORT_THEME.organization;
  workbook.created = new Date();
  const worksheet = workbook.addWorksheet(sheetName);
  return { workbook, worksheet };
}

export function buildExportSubtitle(extra = '') {
  const exportedAt = `Esportato il ${formatExportTimestamp()}`;
  return extra ? `${extra} · ${exportedAt}` : exportedAt;
}
